import { useMemo, useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { ConfirmationDialog, useActionBarContribution, type ActionBarAction } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { StatusVariant } from '~enums';
import KeyTypeEnum from '~enums/keyTypeEnum';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';
import { decapitalizeString } from '~utils/stringUtils';

import EditKeyForm from './forms/editKeyForm';
import Key from '../../../interfaces/key';
import AssignKeyFormResults from '../interfaces/assignKeyFormResults';
import KeysService from '../services/keysService';
import AssignKeyForm from './forms/assignKeyForm';

const organisationService = getService(OrganisationsService);
const keysService = getService(KeysService);

export type KeyActionBarActions =
	| 'unassign'
	| 'showUnassignConfirmation'
	| 'changeResourceGroup'
	| 'assign'
	| 'showAssign'
	| 'edit'
	| 'delete';
interface KeyActionsBarProps {
	keyId: string;
	organisationId?: string | undefined;
	showUnassignButton?: boolean;
	showAssignButton?: boolean;
	showEditButton?: boolean;
	showDeleteButton?: boolean;
	showChangeResourceGroupButton?: boolean;
}

const KeyActionsBar = ({
	keyId,
	organisationId,
	showUnassignButton,
	showAssignButton,
	showEditButton = true,
	showDeleteButton = true,
	showChangeResourceGroupButton = false,
}: KeyActionsBarProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [userAction, setUserAction] = useState<KeyActionBarActions | null>(null);
	const [isUpdating, setIsUpdating] = useState<boolean>(false);

	const clearUserAction = () => {
		setUserAction(null);
	};
	const { data: keyData, mutate: mutateKey } = useSWR(
		keysService.getKeyByIdCacheKey(keyId, organisationId),
		([_, id, organisationId]) =>
			id ? keysService.getKeyById(id, organisationId ?? undefined) : null,
		{ revalidateOnMount: true }
	);

	const updateKey = async (key: Key) => {
		setIsUpdating(true);

		await keysService
			.updateKey(key.id, key, organisationId)
			.then((updated) => {
				mutateKey(updated, { revalidate: false });
				enqueueSnackbar(
					t('successGeneric', {
						ns: 'toast',
						action: t('edited', { ns: 'status' }),
						value: t('key.labels.single', { ns: 'entities' }),
					}),
					{
						variant: 'success',
					}
				);
			})
			.catch((error: ApiError) => {
				enqueueSnackbar(
					t('errorGeneric', {
						ns: 'toast',
						action: t('edited', { ns: 'status' }),
						value: t('key.labels.single', { ns: 'entities' }),
					}) + ` ${error.getDescription()}`,
					{
						variant: 'error',
					}
				);
			})
			.finally(() => {
				clearUserAction();
				setIsUpdating(false);
			});
	};

	const confirmUnassign = async () => {
		setIsUpdating(true);

		await organisationService
			.unassignKeys([keyId])
			.then(() => {
				setUserAction(null);
				navigate(
					{
						pathname: `/${pagePathSegments.Keys}/${keyId}/${pagePathSegments.General}`,
						search: createSearchParams({
							[queryParameters.OverwrittenClientId]: generalInformation.manufacturingOrganisationId,
						}).toString(),
					},
					{ replace: true }
				);
				enqueueSnackbar(t('keyUnassignedSuccesfully', { ns: 'toast' }), { variant: 'success' });
			})
			.catch((_reason) => {
				enqueueSnackbar(t('keyFailedToUnassign', { ns: 'toast' }), { variant: 'error' });
			})
			.finally(() => {
				setIsUpdating(false);
			});
	};

	const confirmDelete = async () => {
		setIsUpdating(true);
		await keysService
			.deleteKey(keyId, organisationId)
			.then(() => {
				enqueueSnackbar(t('keyDeletedSuccesfully', { ns: 'toast' }), { variant: 'success' });
				navigate(`/${pagePathSegments.Keys}`, { replace: true });
			})
			.catch((_reason) => {
				enqueueSnackbar(t('keyFailedToDelete', { ns: 'toast' }), { variant: 'error' });
			})
			.finally(() => {
				setIsUpdating(false);
			});
	};

	const handleAssignKey = async (assignKeyFormResults: AssignKeyFormResults) => {
		const resourceGroupService = getService(ResourceGroupService);
		let assignedToOrganisationSuccessfully = true;

		setIsUpdating(true);

		if (assignKeyFormResults.organisationId != undefined) {
			await organisationService
				.assignKeysToOrganisation([keyId], assignKeyFormResults.organisationId)
				.then(() => {
					if (!assignKeyFormResults.resourceGroupId) {
						setUserAction(null);
						navigate(
							{
								pathname: `/${pagePathSegments.Keys}/${keyId}/${pagePathSegments.General}`,
								search: createSearchParams({
									[queryParameters.OverwrittenClientId]: assignKeyFormResults.organisationId ?? '',
								}).toString(),
							},
							{ replace: true }
						);
						enqueueSnackbar(t('keyAssignedSuccesfully', { ns: 'toast' }), {
							variant: 'success',
						});
					}
				})
				.catch((_reason) => {
					setUserAction(null);
					assignedToOrganisationSuccessfully = false;
					enqueueSnackbar(t('keyFailedToAssign', { ns: 'toast' }), {
						variant: 'error',
					});
				});
		}

		if (assignKeyFormResults.resourceGroupId && assignedToOrganisationSuccessfully) {
			await resourceGroupService
				.assignKeysToResourceGroup(
					[keyId],
					assignKeyFormResults.resourceGroupId,
					assignKeyFormResults.organisationId ?? organisationId
				)
				.then(() => {
					setUserAction(null);
					if (assignKeyFormResults.organisationId != undefined) {
						navigate(
							{
								pathname: `/${pagePathSegments.Keys}/${keyId}/${pagePathSegments.General}`,
								search: createSearchParams({
									[queryParameters.OverwrittenClientId]: assignKeyFormResults.organisationId ?? '',
								}).toString(),
							},
							{ replace: true }
						);
					} else {
						mutateKey();
					}
					enqueueSnackbar(t('keyAssignedSuccesfully', { ns: 'toast' }), {
						variant: 'success',
					});
				})
				.catch((_reason) => {
					enqueueSnackbar(t('keyFailedToAssign', { ns: 'toast' }), {
						variant: 'error',
					});
				});
		}
		setIsUpdating(false);
	};

	const actions = useMemo<ActionBarAction[]>(() => {
		const list: ActionBarAction[] = [];
		const isNotVirtualKey = keyData?.type !== KeyTypeEnum.VirtualKey;

		if (showUnassignButton && isNotVirtualKey) {
			list.push({
				id: `key-${keyId}-unassign`,
				label: t('unassign', { ns: 'actions' }),
				icon: <VectorImage name='icon/unlink' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('showUnassignConfirmation'),
			});
		}

		if (showAssignButton && isNotVirtualKey) {
			list.push({
				id: `key-${keyId}-assign`,
				label: t('assign', { ns: 'actions' }),
				icon: <VectorImage name='icon/link' />,
				variant: StatusVariant.Brand,
				onClick: () => setUserAction('assign'),
			});
		}

		if (showEditButton) {
			list.push({
				id: `key-${keyId}-edit`,
				label: t('edit', { ns: 'actions' }),
				icon: <VectorImage name='icon/edit' />,
				onClick: () => setUserAction('edit'),
			});
		}
		if (showChangeResourceGroupButton) {
			list.push({
				id: `key-${keyId}-change-resource-group`,
				label: t('changeResourceGroup', { ns: 'actions' }),
				icon: <VectorImage name='icon/swap' />,
				onClick: () => setUserAction('changeResourceGroup'),
			});
		}

		if (showDeleteButton) {
			list.push({
				id: `key-${keyId}-delete`,
				label: t('delete', { ns: 'actions' }),
				icon: <VectorImage name='icon/bin' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('delete'),
			});
		}

		return list;
	}, [
		showUnassignButton,
		showAssignButton,
		showEditButton,
		showChangeResourceGroupButton,
		showDeleteButton,
		keyId,
		keyData?.type,
		t,
	]);

	useActionBarContribution(actions, { id: `key-actions-${keyId}`, overflowLimit: 1 });

	return (
		<>
			<ConfirmationDialog
				open={userAction === 'showUnassignConfirmation'}
				title={t('key.labels.unassignKeyDialogTitle', { ns: 'entities' })}
				helperText={t('key.labels.unassignKeyDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('unassign', { ns: 'actions' })}
				onConfirm={confirmUnassign}
				onClose={clearUserAction}
				icon={<VectorImage name='icon/unlink' />}
				loading={isUpdating}
				variant={StatusVariant.Destructive}
			/>
			<ConfirmationDialog
				open={userAction === 'delete'}
				title={t('key.labels.deleteKeyDialogTitle', { ns: 'entities' })}
				helperText={t('key.labels.deleteKeyDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('delete', { ns: 'actions' })}
				onConfirm={confirmDelete}
				onClose={clearUserAction}
				icon={<VectorImage name='icon/bin' />}
				loading={isUpdating}
				variant={StatusVariant.Destructive}
			/>
			<FormDialog
				open={userAction === 'edit'}
				title={`${t('edit', { ns: 'actions' })} ${decapitalizeString(t('key.labels.single', { ns: 'entities' }))}`}
				loading={isUpdating}
			>
				<EditKeyForm
					initialKey={keyData ?? ({} as Key)}
					onSubmit={async (updated) => updateKey(updated)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<FormDialog
				open={userAction === 'assign' || userAction === 'changeResourceGroup'}
				title={
					userAction === 'changeResourceGroup' ?
						t('changeResourceGroup', { ns: 'actions' })
					:	t('assignment.action.assignEntity', {
							ns: 'entities',
							entity: t('key.labels.single', { ns: 'entities' }),
						})
				}
				loading={isUpdating}
			>
				<AssignKeyForm
					currentlyAssignedOrganisationId={
						userAction === 'changeResourceGroup' ? organisationId : undefined
					}
					showEditOrganisation={userAction === 'assign'}
					onSubmit={(formData) => {
						handleAssignKey(formData);
					}}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default KeyActionsBar;
